// FTEDN — Chat store (Zustand)
import { create } from 'zustand';
import { apiClient } from '../hooks/useApi';
import { subscribeMessages, unsubscribe } from '../services/firestore-realtime';

export interface ChatChannel {
  id: string;
  type: 'dm' | 'group' | 'event';
  name: string | null;
  memberIds: string[];
  createdBy: string;
  lastMessageAt: string | null;
  createdAt: string;
}

export interface ChatMessage {
  id: string;
  channelId: string;
  senderId: string;
  text: string;
  parentMsgId?: string;
  reactions: Record<string, string[]>;
  mentions: string[];
  hidden: boolean;
  createdAt: string;
}

interface ChatState {
  channels: ChatChannel[];
  activeChannelId: string | null;
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
  fetchChannels: () => Promise<void>;
  setActiveChannel: (id: string) => void;
  subscribeToMessages: (channelId: string) => void;
  unsubscribeFromMessages: () => void;
  fetchMessages: (channelId: string) => Promise<void>;
  sendMessage: (channelId: string, text: string, parentMsgId?: string) => Promise<boolean>;
  createDM: (recipientId: string) => Promise<string | null>;
}

/** Map raw channel doc */
export function mapChannel(raw: Record<string, unknown>): ChatChannel {
  return {
    id: String(raw.id ?? ''),
    type: (raw.type as ChatChannel['type']) ?? 'dm',
    name: raw.name as string | null ?? null,
    memberIds: Array.isArray(raw.memberIds) ? raw.memberIds.map(String) : [],
    createdBy: String(raw.createdBy ?? ''),
    lastMessageAt: raw.lastMessageAt as string | null ?? null,
    createdAt: String(raw.createdAt ?? ''),
  };
}

/** Map raw message doc */
export function mapMessage(raw: Record<string, unknown>): ChatMessage {
  return {
    id: String(raw.id ?? ''),
    channelId: String(raw.channelId ?? ''),
    senderId: String(raw.senderId ?? ''),
    text: String(raw.text ?? ''),
    parentMsgId: raw.parentMsgId as string | undefined,
    reactions: (raw.reactions as Record<string, string[]>) ?? {},
    mentions: Array.isArray(raw.mentions) ? raw.mentions.map(String) : [],
    hidden: Boolean(raw.hidden ?? false),
    createdAt: String(raw.createdAt ?? ''),
  };
}

/** Validate a chat message before sending */
export function validateMessage(text: string): { valid: true } | { valid: false; error: string } {
  if (!text.trim()) return { valid: false, error: 'Message cannot be empty' };
  if (text.length > 4000) return { valid: false, error: 'Message too long (max 4000)' };
  return { valid: true };
}

/** Parse @mentions from text */
export function parseMentions(text: string): string[] {
  const matches = text.match(/@(\w+)/g);
  if (!matches) return [];
  return [...new Set(matches.map(m => m.slice(1)))];
}

/** Get channel display name for DMs */
export function getChannelDisplayName(
  channel: ChatChannel,
  currentUserId: string,
  userNames: Record<string, string>,
): string {
  if (channel.name) return channel.name;
  if (channel.type === 'dm') {
    const otherId = channel.memberIds.find(id => id !== currentUserId) ?? '';
    return userNames[otherId] ?? 'Unknown';
  }
  return `Group (${channel.memberIds.length})`;
}

export const useChatStore = create<ChatState>((set, get) => ({
  channels: [],
  activeChannelId: null,
  messages: [],
  loading: false,
  error: null,

  fetchChannels: async () => {
    set({ loading: true, error: null });
    try {
      const res = await apiClient.get('/chat/channels');
      if (res.ok && Array.isArray(res.channels)) {
        set({ channels: res.channels.map(mapChannel), loading: false });
      } else {
        set({ error: 'Failed to load channels', loading: false });
      }
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  setActiveChannel: (id) => {
    // Unsubscribe from previous channel
    if (get().activeChannelId) {
      unsubscribe(`messages:${get().activeChannelId}`);
    }
    set({ activeChannelId: id });
    get().subscribeToMessages(id);
  },

  subscribeToMessages: (channelId: string) => {
    subscribeMessages(
      channelId,
      (docs) => set({ messages: docs.map(mapMessage) }),
      (err) => {
        console.error('[chat] realtime error:', err.message);
        get().fetchMessages(channelId);
      },
    );
  },

  unsubscribeFromMessages: () => {
    const id = get().activeChannelId;
    if (id) unsubscribe(`messages:${id}`);
  },

  fetchMessages: async (channelId) => {
    try {
      const res = await apiClient.get(`/chat/channels/${channelId}/messages`);
      if (res.ok && Array.isArray(res.messages)) {
        set({ messages: res.messages.map(mapMessage) });
      }
    } catch { /* silent */ }
  },

  sendMessage: async (channelId, text, parentMsgId) => {
    const validation = validateMessage(text);
    if (!validation.valid) return false;

    try {
      const res = await apiClient.post(`/chat/channels/${channelId}/messages`, { text, parentMsgId });
      if (res.ok) {
        // Optimistic append
        const newMsg: ChatMessage = {
          id: res.messageId ?? `tmp-${Date.now()}`,
          channelId, senderId: '', text,
          reactions: {}, mentions: parseMentions(text),
          hidden: false, createdAt: new Date().toISOString(),
          parentMsgId,
        };
        set({ messages: [...get().messages, newMsg] });
        return true;
      }
      return false;
    } catch {
      return false;
    }
  },

  createDM: async (recipientId) => {
    try {
      const res = await apiClient.post('/chat/channels', {
        type: 'dm', memberIds: [recipientId],
      });
      if (res.ok && res.channelId) {
        get().fetchChannels();
        return res.channelId;
      }
      return null;
    } catch {
      return null;
    }
  },
}));
